// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsConfig
 * @package @geenius/analytics
 * @description Provides the shared analytics configuration builder and the
 * default environment presets used by the tracker and provider integrations.
 * The fluent builder keeps package consumers on a typed, privacy-aware config path.
 */

import type {
  AnalyticsConfig,
  AnalyticsStorageAdapter,
  ProviderConfig,
  ProviderName,
} from "./types.js";

export type AnalyticsConfigurationBundle = {
  analytics: AnalyticsConfig;
  providers: Partial<Record<ProviderName, ProviderConfig>>;
  storage?: AnalyticsStorageAdapter | AnalyticsStorageAdapter[];
};

/**
 * Fluent builder for shared analytics configuration and provider registration.
 *
 * @example
 * ```ts
 * const config = createAnalyticsConfig()
 *   .withProvider('posthog', { provider: 'posthog', apiKey: 'phc_xxx' })
 *   .withPageTracking(true)
 *   .withClickTracking(true)
 *   .build()
 * ```
 */
export class AnalyticsConfigBuilder {
  private config: AnalyticsConfig = {
    trackPageViews: true,
    trackClicks: false,
    trackErrors: false,
    trackPerformance: false,
    trackSessionStart: true,
    trackNavigation: true,
    allowPreConsentTracking: false,
    batchSize: 1,
    flushIntervalMs: 0,
    eventThrottleMs: 0,
    persistOfflineQueue: true,
    offlineQueueKey: "geenius:analytics:offline-queue",
    replayOnReconnect: true,
    retentionDays: 365,
    respectDnt: true,
    sessionTimeout: 30 * 60 * 1000,
    excludePaths: [],
    debug: false,
    maskPii: true,
    maskIp: true,
    anonymize: false,
  };

  private providers: Map<ProviderName, ProviderConfig> = new Map();
  private storage?: AnalyticsStorageAdapter | AnalyticsStorageAdapter[];

  /**
   * Sets whether shared trackers should record page-view events.
   *
   * @param enabled Whether page-view tracking is enabled.
   * @returns The current builder instance for fluent chaining.
   */
  withPageTracking(enabled: boolean): this {
    this.config.trackPageViews = enabled;
    return this;
  }

  /**
   * Sets whether shared trackers should record click events.
   *
   * @param enabled Whether click tracking is enabled.
   * @returns The current builder instance for fluent chaining.
   */
  withClickTracking(enabled: boolean): this {
    this.config.trackClicks = enabled;
    return this;
  }

  /**
   * Sets whether shared trackers should record global browser error events.
   *
   * @param enabled Whether browser error tracking is enabled.
   * @returns The current builder instance for fluent chaining.
   */
  withErrorTracking(enabled: boolean): this {
    this.config.trackErrors = enabled;
    return this;
  }

  /**
   * Sets whether shared trackers should record browser performance metrics.
   *
   * @param enabled Whether performance tracking is enabled.
   * @returns The current builder instance for fluent chaining.
   */
  withPerformanceTracking(enabled: boolean): this {
    this.config.trackPerformance = enabled;
    return this;
  }

  /**
   * Sets whether shared trackers should respect the browser Do Not Track signal.
   *
   * @param enabled Whether Do Not Track should suppress analytics collection.
   * @returns The current builder instance for fluent chaining.
   */
  withRespectDnt(enabled: boolean): this {
    this.config.respectDnt = enabled;
    return this;
  }

  /**
   * Sets the analytics session timeout in milliseconds.
   *
   * @param ms Session timeout in milliseconds.
   * @returns The current builder instance for fluent chaining.
   */
  withSessionTimeout(ms: number): this {
    this.config.sessionTimeout = ms;
    return this;
  }

  /**
   * Sets the number of queued track/page payloads that should trigger a flush.
   *
   * @param size Queue size threshold; values below 1 are normalized by the tracker.
   * @returns The current builder instance for fluent chaining.
   */
  withBatchSize(size: number): this {
    this.config.batchSize = size;
    return this;
  }

  /**
   * Sets the interval in milliseconds used to flush queued analytics payloads.
   *
   * @param ms Flush interval in milliseconds; zero disables interval flushing.
   * @returns The current builder instance for fluent chaining.
   */
  withFlushInterval(ms: number): this {
    this.config.flushIntervalMs = ms;
    return this;
  }

  /**
   * Sets a per-event-name throttle window for custom analytics events.
   *
   * @param ms Throttle window in milliseconds; zero disables throttling.
   * @returns The current builder instance for fluent chaining.
   */
  withEventThrottle(ms: number): this {
    this.config.eventThrottleMs = ms;
    return this;
  }

  /**
   * Enables or disables durable persistence for queued offline analytics payloads.
   *
   * @param enabled Whether queued payloads should be persisted locally.
   * @returns The current builder instance for fluent chaining.
   */
  withOfflineQueuePersistence(enabled: boolean): this {
    this.config.persistOfflineQueue = enabled;
    return this;
  }

  /**
   * Sets the browser storage key used for the durable offline queue.
   *
   * @param key Storage key used to persist queued analytics payloads.
   * @returns The current builder instance for fluent chaining.
   */
  withOfflineQueueKey(key: string): this {
    this.config.offlineQueueKey = key;
    return this;
  }

  /**
   * Enables or disables automatic queue replay when the browser reconnects.
   *
   * @param enabled Whether queued payloads should flush on reconnect.
   * @returns The current builder instance for fluent chaining.
   */
  withReplayOnReconnect(enabled: boolean): this {
    this.config.replayOnReconnect = enabled;
    return this;
  }

  /**
   * Sets the retention window used by storage/provider purge hooks.
   *
   * @param days Number of days to retain analytics records and aggregates.
   * @returns The current builder instance for fluent chaining.
   */
  withRetentionDays(days: number): this {
    this.config.retentionDays = days;
    return this;
  }

  /**
   * Adds routes that should be excluded from automatic page tracking.
   *
   * @param paths Route prefixes that should be skipped by the tracker.
   * @returns The current builder instance for fluent chaining.
   */
  withExcludePaths(paths: string[]): this {
    this.config.excludePaths = paths;
    return this;
  }

  /**
   * Enables or disables verbose analytics debug behavior.
   *
   * @param enabled Whether debug behavior should be enabled.
   * @returns The current builder instance for fluent chaining.
   */
  withDebug(enabled: boolean): this {
    this.config.debug = enabled;
    return this;
  }

  /**
   * Enables or disables masking of sensitive payload values.
   *
   * @param enabled Whether PII masking is enabled.
   * @returns The current builder instance for fluent chaining.
   */
  withMaskPii(enabled: boolean): this {
    this.config.maskPii = enabled;
    return this;
  }

  /**
   * Enables or disables IP truncation for event and page-view payloads.
   *
   * @param enabled Whether IP-shaped fields should be truncated before dispatch.
   * @returns The current builder instance for fluent chaining.
   */
  withIpMasking(enabled: boolean): this {
    this.config.maskIp = enabled;
    return this;
  }

  /**
   * Enables or disables anonymization of identifiers and payload strings.
   *
   * @param enabled Whether anonymization is enabled.
   * @returns The current builder instance for fluent chaining.
   */
  withAnonymize(enabled: boolean): this {
    this.config.anonymize = enabled;
    return this;
  }

  /**
   * Registers a provider configuration in the current bundle.
   *
   * @param name Provider name used as the registry key.
   * @param config Provider configuration associated with the given name.
   * @returns The current builder instance for fluent chaining.
   */
  withProvider(name: ProviderName, config: ProviderConfig): this {
    this.providers.set(name, config);
    return this;
  }

  /**
   * Registers one or more self-hosted storage adapters that persist sanitized
   * analytics data before optional third-party sync.
   *
   * @param storage Storage adapter or adapters used by the tracker.
   * @returns The current builder instance for fluent chaining.
   */
  withStorage(
    storage: AnalyticsStorageAdapter | AnalyticsStorageAdapter[],
  ): this {
    this.storage = storage;
    return this;
  }

  /**
   * Produces the final analytics configuration bundle.
   *
   * @returns The analytics settings plus provider registry for tracker creation.
   */
  build(): AnalyticsConfigurationBundle {
    return {
      analytics: this.config,
      providers: Object.fromEntries(this.providers),
      storage: this.storage,
    };
  }
}

/**
 * Creates a new fluent analytics configuration builder.
 *
 * @returns A fresh analytics configuration builder instance.
 */
export function createAnalyticsConfig(): AnalyticsConfigBuilder {
  return new AnalyticsConfigBuilder();
}

/**
 * Default analytics configuration bundles for common runtime environments.
 */
export const defaultConfigs: Record<
  "development" | "production" | "testing",
  AnalyticsConfigurationBundle
> = {
  development: createAnalyticsConfig()
    .withDebug(true)
    .withPageTracking(true)
    .withClickTracking(true)
    .withRespectDnt(false)
    .build(),
  production: createAnalyticsConfig()
    .withPageTracking(true)
    .withClickTracking(false)
    .withRespectDnt(true)
    .withMaskPii(true)
    .withAnonymize(true)
    .withDebug(false)
    .build(),
  testing: createAnalyticsConfig()
    .withPageTracking(false)
    .withClickTracking(false)
    .withDebug(false)
    .build(),
};

/**
 * Returns the default analytics configuration bundle for the requested environment.
 *
 * @param env Runtime environment name used to select the preset.
 * @returns The analytics configuration bundle for the requested environment.
 */
export function getDefaultConfig(
  env: "development" | "production" | "testing" = "production",
): AnalyticsConfigurationBundle {
  return defaultConfigs[env];
}
